import { useEffect, useRef, useState } from "react";

/**
 * Debounce a value so a query fires when typing pauses, not per keystroke.
 *
 * A Databricks warehouse charges roughly a second of latency per query regardless
 * of result size, so an un-debounced search box queues a query per character and
 * the answers arrive out of order.
 */
export function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * One page of a server-filtered, server-sorted list.
 *
 * Shared by any list too large to hold in the browser. It owns the three things
 * that are easy to get subtly wrong per page:
 *
 *   - offset resets to 0 whenever the filters change, so changing a filter cannot
 *     leave you on page 7 of a 2-page result staring at an empty table
 *   - the previous rows stay on screen while the next page loads, instead of the
 *     list blanking for a second on every interaction
 *   - a slow response for filters you have already moved on from is discarded,
 *     rather than overwriting the newer result it arrives after
 *
 * @param fetcher  (params) => Promise<{items, total}>
 * @param params   query parameters; any change resets to the first page
 * @param limit    page size
 */
export function useServerList(fetcher, params, limit = 50) {
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [meta,    setMeta]    = useState({});   // any extra aggregates the API returns
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  // Bumped by refresh(). Re-setting offset to its current value would not work —
  // React skips the render when the state is unchanged, so nothing would refetch.
  const [nonce,   setNonce]   = useState(0);

  // Serialised so a new object with identical values does not refetch.
  const key = JSON.stringify(params);
  const latest = useRef(0);

  // Reset to the first page when the filters change — but not when only the page
  // changed, which is why this watches the params and not the offset.
  useEffect(() => { setOffset(0); }, [key]);

  useEffect(() => {
    const request = ++latest.current;
    setLoading(true);
    setError(null);

    fetcher({ ...params, limit, offset })
      .then((data) => {
        if (request !== latest.current) return;   // a newer request is in flight
        const rows = Array.isArray(data) ? data : (data.items ?? []);
        const { items: _drop, total: _t, ...rest } = Array.isArray(data) ? {} : data;
        setItems(rows);
        setTotal(Array.isArray(data) ? rows.length : (data.total ?? rows.length));
        setMeta(rest);
        setLoading(false);
      })
      .catch((err) => {
        if (request !== latest.current) return;
        setError(err?.message || "Failed to load");
        setLoading(false);
      });
  }, [key, offset, limit, nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => setNonce((n) => n + 1);

  // Two states a single `loading` flag conflates:
  //   initialLoading — nothing has been shown yet, so a placeholder is all there is
  //   loading        — rows are on screen and are being replaced
  // Rendering the placeholder for both is what makes a sort click blank the page
  // for a round trip. Key the empty state on initialLoading and dim the existing
  // rows for the rest.
  const initialLoading = loading && items.length === 0;

  return { items, total, meta, offset, setOffset, loading, initialLoading, error, refresh };
}
